import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/common/Breadcrumb';
import CustomDropdown from '@/components/common/CustomDropdown';
import Card from '@/components/ui/cards/Card';
import CardHeader from '@/components/ui/cards/CardHeader';
import PerPageSelection from '@/components/ui/tables/PerPageSelection';
import { useSelector, useDispatch } from 'react-redux';
import validationErrors from '@/lib/validationErrors';
import { getOfferService, deleteOfferService } from '@/services/OfferServices';
import { cn } from '@/lib/utils';
import DataTableFilter from './component/DataTableFilter';
import DataTable from './component/DataTable';
import AddEditDrawer from './component/AddEditDrawer';
import PritntPdfTable from '@/components/common/PritntPdfTable';
import PermissionCheck from '@/components/common/PermissionCheck';

const OffersPage = ({ pageTitle }) => {
    const dispatch = useDispatch();
    const { printTableRef, handleDownloadPdf } = PritntPdfTable('Offers Data');
    const [dataSource, setDataSource] = useState([]);
    const [totalRecord, setTotalRecord] = useState(0);
    const [activeDrawer, setActiveDrawer] = useState(null);
    const [editDataId, setEditDataId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const filterDivRef = useRef(null);
    const [dataFilter, setDataFilter] = useState({
        search: '',
        page: 1,
        limit: 10,
        extra_filter: {},
    });
    const tableColumn = [
        { name: 'Name', isIncludePrint: true },
        { name: 'Discount Amount (%)', isIncludePrint: true },
        { name: 'Start Date', isIncludePrint: true },
        { name: 'End Date', isIncludePrint: true },
        { name: 'Status', isIncludePrint: true },
        { name: 'Action', isIncludePrint: false },
    ];

    useEffect(() => {
        getTableData();
    }, [dataFilter]);

    const handleDrawerOpen = (editId) => {
        if (editId && editId != '') {
            setEditDataId(editId);
        } else {
            setEditDataId('');
        }
        setActiveDrawer(true);
        document.body.style.overflowY = 'hidden';
    };

    const onChangeFilter = (name, value) => {
        setDataFilter({ ...dataFilter, [name]: value });
    };

    const handleFilterToggle = () => {
        if (filterDivRef.current) {
            const height = filterDivRef.current.scrollHeight;
            if (!isActive) {
                setIsActive(true);
                filterDivRef.current.style.height = `100%`;
            } else {
                setIsActive(false);
                filterDivRef.current.style.height = '0px';
            }
        }
    };

    const getTableData = () => {
        setIsLoading(true);
        dispatch(getOfferService(dataFilter))
            .then((res) => {
                setTotalRecord(res?.data?.data?.total);
                setDataSource(res?.data?.data?.data);
                setIsLoading(false);
            })
            .catch(({ message, errorData, statusCode }) => {
                validationErrors(errorData, message);
                setIsLoading(false);
            });
    };

    const deleteRecord = (deleteId) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
        }).then((result) => {
            if (result.isConfirmed) {
                setIsLoading(true);
                dispatch(deleteOfferService(deleteId))
                    .then((res) => {
                        Swal.fire({
                            title: 'Deleted!',
                            text: 'Your record has been deleted.',
                            icon: 'success',
                        });
                        getTableData(dataFilter);
                        setIsLoading(false);
                    })
                    .catch(({ message, errorData, statusCode }) => {
                        validationErrors(errorData, message);
                        setIsLoading(false);
                    });
            }
        });
    };

    return (
        <>
            <Breadcrumb pageTitle={pageTitle} />
            <Card>
                <CardHeader>
                    <h3 className="db-card-title">{pageTitle}</h3>
                    <div className="db-card-filter">
                        <div className="db-field-down-arrow">
                            <PerPageSelection onChangeParPage={onChangeFilter} limit={dataFilter.limit} />
                        </div>
                        <button className="db-card-filter-btn table-filter-btn" onClick={handleFilterToggle}>
                            <i className="lab lab-filter lab-font-size-14"></i>
                            <span>Filter</span>
                        </button>
                        <CustomDropdown
                            buttonText={'Export'}
                            buttonIcon={'lab-file-export'}
                            dropdownList={[
                                {
                                    icon: 'lab-printer-line',
                                    text: 'Print',
                                    dropDownAction: handleDownloadPdf,
                                },
                                // {
                                //     icon: 'lab-file-excel lab-font-size-15',
                                //     text: 'Excel',
                                // },
                            ]}
                        />
                        <PermissionCheck permsission={'create_offers'}>
                            <button type="button" onClick={() => handleDrawerOpen('')} className={'db-btn h-[37px] text-white bg-primary'}>
                                <i className={'lab lab-add-circle-line'}></i>
                                Add Offer
                            </button>
                        </PermissionCheck>
                    </div>
                </CardHeader>
                <div className={cn('table-filter-div', isActive ? 'active' : '')} ref={filterDivRef}>
                    <DataTableFilter onChangeFilter={onChangeFilter} />
                </div>
                <div ref={printTableRef}>
                    <DataTable
                        isLoading={isLoading}
                        tableColumn={tableColumn}
                        dataSource={dataSource}
                        totalRecord={totalRecord}
                        dataFilter={dataFilter}
                        onChangePage={onChangeFilter}
                        handleDrawerOpen={handleDrawerOpen}
                        deleteRecord={deleteRecord}
                    />
                </div>
                <AddEditDrawer titleText={pageTitle} getTableData={getTableData} activeDrawer={activeDrawer} setActiveDrawer={setActiveDrawer} editDataId={editDataId} />
            </Card>
        </>
    );
};

export default OffersPage;
